import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { dailyService } from "../services/soar";
import FloatingInput from "../common/FloatingInput";
import DailyService from "./DailyService";

function CensusCell({ bedAssignment, service, setData, showConfirmed }) {
  const [show, setShow] = useState(true);
  const [hide, setHide] = useState(false);
  const [showDailyService, setShowDailyService] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [view, setView] = useState("Bed");

  useEffect(() => {
    if (show === false) {
      setTimeout(() => {
        setHide(true);
      }, 300); // Match the duration of reactFade-exit animation
    }
  }, [show]);

  useEffect(() => {
    setHide(showConfirmed === false && service.confirmed === true);
  }, [showConfirmed]);

  function convertDateFormat(dateString) {
    const parts = dateString.split("/"); // Splits "MM/DD/YYYY" into ["MM", "DD", "YYYY"]
    const formattedDate = [parts[2], parts[0], parts[1]].join("-"); // Rearranges and joins with hyphens
    return formattedDate;
  }

  const payorCheck = bedAssignment.payors?.find(
    (payor) =>
      new Date(payor.effective) <= new Date(service.serviceDate) &&
      (!payor.expiration ||
        new Date(payor.expiration) >= new Date(service.serviceDate))
  );

  const modalityCheck = bedAssignment.modalities?.find(
    (modality) =>
      modality.ccx_clientprogram?.id === service.clientProgram &&
      new Date(convertDateFormat(modality.ccx_startdate?.date)) <=
        new Date(service.serviceDate) &&
      (!modality.ccx_enddate ||
        new Date(convertDateFormat(modality.ccx_enddate.date)) >=
          new Date(service.serviceDate))
  );

  const tierCheck = bedAssignment.residenceRelated?.find(
    (tier) =>
      tier.type === "FP Tier" &&
      new Date(tier.effective) <= new Date(service.serviceDate) &&
      (!tier.expiration ||
        new Date(tier.expiration) >= new Date(service.serviceDate))
  );

  const handleServiceStatusChange = async (bedAssignment, service, e) => {
    const updatedService = {
      ...service,
      serviceStatus: e.target.value,
    };
    dailyService({
      command: "update",
      ...updatedService,
    })
      .then((response) => {
        if (response.code === "success") {
          setData((prevData) =>
            prevData.map((item) =>
              item.id === bedAssignment.id
                ? {
                    ...item,
                    services: item.services.map((s) =>
                      s.id === service.id ? updatedService : s
                    ),
                  }
                : item
            )
          );
          toast.success("Service status updated.");
        } else {
          toast.error(`Error updating service status: ${response.message}`);
        }
      })
      .catch((error) => {
        toast.error(`Error updating service status: ${error.message}`);
      });
  };

  const handleConfirmService = async (bedAssignment, service, action) => {
    setConfirming(true);
    const updatedService = {
      ...service,
      confirmed: action === "confirm" ? true : false,
    };
    dailyService({
      command: action === "confirm" ? "confirm" : "unconfirm",
      ...updatedService,
    })
      .then((response) => {
        if (response.code === "success") {
          setData((prevData) =>
            prevData.map((item) =>
              item.id === bedAssignment.id
                ? {
                    ...item,
                    services: item.services.map((s) =>
                      s.id === service.id ? updatedService : s
                    ),
                  }
                : item
            )
          );
          toast.success(
            `Service ${action === "confirm" ? "confirmed" : "unconfirmed"}.`
          );
          setShow(action !== "confirm");
          setTimeout(() => {
            setConfirming(false);
          }, 500);
        } else {
          toast.error(
            `Error ${
              action === "confirm" ? "confirming" : "unconfirming"
            } service: ${response.message}`
          );
          setTimeout(() => {
            setConfirming(false);
          }, 500);
        }
      })
      .catch((error) => {
        toast.error(
          `Error ${
            action === "confirm" ? "confirming" : "unconfirming"
          } service: ${error.message}`
        );
        setTimeout(() => {
          setConfirming(false);
        }, 500);
      });
  };

  return (
    <>
      <Modal
        show={showDailyService}
        onHide={() => setShowDailyService(false)}
        animation={true}
        size="xl"
      >
        <Modal.Header closeButton className="bg-safy-info text-white">
          <Modal.Title>Service Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div
              className={
                view === "Bed"
                  ? "col btn btn-primary m-1"
                  : "col btn btn-secondary m-1"
              }
              onClick={() => setView("Bed")}
            >
              Bed View
            </div>
            <div
              className={
                view === "Billing"
                  ? "col btn btn-primary m-1"
                  : "col btn btn-secondary m-1"
              }
              onClick={() => setView("Billing")}
            >
              Billing View
            </div>
            <div
              className={
                view === "Invoice/Payments"
                  ? "col btn btn-primary m-1"
                  : "col btn btn-secondary m-1"
              }
              onClick={() => setView("Invoice/Payments")}
            >
              Invoice/Payments View
            </div>
          </div>
          <DailyService
            service={service}
            view={view}
            clientInfo={{
              payors: bedAssignment.payors,
              modalities: bedAssignment.modalities,
              mpi: bedAssignment.mpi,
              dob: bedAssignment.dob,
            }}
            setData={setData}
            bedAssignment={bedAssignment}
          />
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowDailyService(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {hide === false || showConfirmed === true ? (
        <div
          key={service.id}
          className={`m-1 reactFade ${
            !show && showConfirmed === false ? "reactFade-exit" : ""
          }`}
        >
          <div
            className={`rounded p-2 ${
              service.confirmed
                ? "safy-border-4 border-secondary"
                : "border border-dark"
            }`}
          >
            <div className="">
              <div className="row">
                <div className="">
                  <div
                    className="float-start fw-bold text-decoration-underline"
                    role="button"
                    onClick={() => setShowDailyService(true)}
                  >
                    {DateTime.fromISO(service.serviceDate).toLocaleString(
                      DateTime.DATE_SHORT
                    )}
                  </div>{" "}
                  <div
                    className={` ${
                      !show && showConfirmed === false ? "d-none" : ""
                    }`}
                  >
                    {(bedAssignment.companyName.includes("Alabama") ||
                      bedAssignment.companyName.includes("Kentucky") ||
                      bedAssignment.companyName.includes("South Carolina")) && (
                      <div
                        className={`fw-bold rounded border border-dark text-white mx-1 px-1 float-end ${
                          tierCheck !== undefined ? "bg-success" : "bg-danger"
                        } `}
                        title={
                          tierCheck !== undefined
                            ? tierCheck.value
                            : "No Tier for Service Day"
                        }
                        role="button"
                        onClick={() =>
                          tierCheck !== undefined
                            ? toast(
                                <div className="card card-safy">
                                  <div className="card-header bg-safy1-1 text-white">
                                    TIER: {tierCheck.value}
                                  </div>
                                  <div className="card-body">
                                    <div className="text-primary">
                                      Effective
                                    </div>
                                    <div className="fw-bold">
                                      {tierCheck.effective}
                                    </div>
                                    {tierCheck.expiration && (
                                      <>
                                        <div className="text-primary">
                                          Expiration
                                        </div>
                                        <div className="fw-bold">
                                          {tierCheck.expiration}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )
                            : toast.error(<div>No Tier for Service Date</div>)
                        }
                      >
                        T
                      </div>
                    )}
                    <div
                      className={`fw-bold rounded border border-dark text-white mx-1 px-1 float-end ${
                        modalityCheck !== undefined ? "bg-success" : "bg-danger"
                      }`}
                      title={
                        modalityCheck
                          ? modalityCheck.ccx_caremodality?.name
                          : "No Modality for Service Day"
                      }
                      role="button"
                      onClick={() =>
                        modalityCheck !== undefined
                          ? toast(
                              <div className="card card-safy">
                                <div className="card-header bg-safy1-1 text-white">
                                  {modalityCheck.ccx_caremodality?.name}
                                </div>
                                <div className="card-body">
                                  <div className="text-primary">Effective</div>
                                  <div className="fw-bold">
                                    {modalityCheck.ccx_startdate?.date}
                                  </div>
                                  {modalityCheck.ccx_enddate && (
                                    <>
                                      <div className="text-primary">
                                        End Date
                                      </div>
                                      <div className="fw-bold">
                                        {modalityCheck.ccx_enddate.date}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )
                          : toast.error(<div>No Modality for Service Date</div>)
                      }
                    >
                      M
                    </div>
                    <div
                      className={`fw-bold rounded border border-dark text-white mx-1 px-1 float-end ${
                        payorCheck !== undefined ? "bg-success" : "bg-danger"
                      }`}
                      title={
                        payorCheck !== undefined
                          ? payorCheck.payorName
                          : "No Payor for Service Day"
                      }
                      role="button"
                      onClick={() =>
                        payorCheck !== undefined
                          ? toast(
                              <div className="card card-safy">
                                <div className="card-header bg-safy1-1 text-white">
                                  {payorCheck.payorName}
                                </div>
                                <div className="card-body">
                                  <div className="text-primary">Group Name</div>
                                  <div className="fw-bold">
                                    {payorCheck?.groupPlanName}
                                  </div>
                                  <div className="text-primary">Effective</div>
                                  <div className="fw-bold">
                                    {DateTime.fromISO(
                                      payorCheck?.effective
                                    ).toLocaleString(DateTime.DATE_SHORT)}
                                  </div>
                                  {payorCheck?.expiration && (
                                    <>
                                      <div className="text-primary">
                                        Expiration
                                      </div>
                                      <div className="fw-bold">
                                        {DateTime.fromISO(
                                          payorCheck?.expiration
                                        ).toLocaleString(DateTime.DATE_SHORT)}
                                      </div>
                                    </>
                                  )}
                                  {payorCheck.customInsuranceId1 && (
                                    <>
                                      <div className="text-primary">ID 1</div>
                                      <div className="fw-bold">
                                        {payorCheck.customInsuranceId1}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )
                          : toast.error(<div>No Payor for Service Date</div>)
                      }
                    >
                      P
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div
                  className={` ${
                    !show && showConfirmed === false ? "d-none" : ""
                  }`}
                >
                  <FloatingInput
                    type="select"
                    name="serviceStatus"
                    label="Service Status"
                    options={
                      bedAssignment.serviceConfigurationValue?.serviceStatuses?.map(
                        (status) => ({
                          _id: status,
                          name: status,
                        })
                      ) || []
                    }
                    value={service.serviceStatus}
                    onChange={(e) => {
                      handleServiceStatusChange(bedAssignment, service, e);
                    }}
                    disabled={service.confirmed}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <i
                    className={`fas fa-note-sticky ms-2 ${
                      service.note ? "text-warning" : "text-secondary"
                    }`}
                    onClick={() => setShowDailyService(true)}
                    title={service.note}
                    role="button"
                  ></i>
                </div>
                <div className="col">
                  {confirming === false && (
                    <>
                      {service.confirmed ? (
                        <i
                          className="fas fa-square-xmark me-2 float-end"
                          onClick={() =>
                            handleConfirmService(
                              bedAssignment,
                              service,
                              "unconfirm"
                            )
                          }
                        />
                      ) : (
                        <i
                          className="far fa-square text-dark me-2 float-end"
                          onClick={() =>
                            handleConfirmService(
                              bedAssignment,
                              service,
                              "confirm"
                            )
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default CensusCell;
